<template>
  <div class="location-selector text-sm">
    <Accordion>
      <AccordionItem
        v-model:is-open="accordionState.regions"
        :title="$t('ui.entities.locationSelection.region', 2)"
      >
        <div
          v-if="accessSelector.regions.length > 1"
          class="mb-2 text-secondary"
        >
          <Checkbox
            :is-checked="areAllSelected('regions')"
            :label="areAllSelected('regions') ? $t('ui.actions.deselectAll') : $t('ui.actions.selectAll')"
            checkbox-color="utility-gray-500"
            @change="selectAll('regions')"
          />
        </div>
        <div
          v-for="item in accessSelector.regions"
          :key="item.id"
          class="mb-1"
        >
          <Checkbox
            :is-checked="accessSelected.regions.includes(item.id)"
            :label="item.name"
            @change="toggleItem('regions', item.id)"
          />
        </div>
        <div v-if="accessSelector.regions.length === 0">{{ $t('ui.support.assistance') }}</div>
      </AccordionItem>

      <AccordionItem
        v-model:is-open="accordionState.manufacturers"
        :title="$t('ui.entities.manufacturer', 2)"
      >
        <div
          v-if="accessSelector.manufacturers.length > 1"
          class="mb-2 text-secondary"
        >
          <Checkbox
            :is-checked="areAllSelected('manufacturers')"
            :label="areAllSelected('manufacturers') ? $t('ui.actions.deselectAll') : $t('ui.actions.selectAll')"
            checkbox-color="utility-gray-500"
            @change="selectAll('manufacturers')"
          />
        </div>
        <div
          v-for="item in accessSelector.manufacturers"
          :key="'manufacturer-' + item.id"
          class="mb-1"
        >
          <Checkbox
            :is-checked="accessSelected.manufacturers.includes(item.id)"
            :label="item.name"
            @change="toggleItem('manufacturers', item.id)"
          />
        </div>
        <div
          v-if="accessSelector.manufacturers.length === 0"
          class="text-secondary"
        >
          {{
            $t(
              'ui.entities.locationSelection.sentences.selectRegions',
              {
                item: $t('ui.entities.locationSelection.region', 2)
              },
              2
            )
          }}
        </div>
      </AccordionItem>

      <AccordionItem
        v-model:is-open="accordionState.groups"
        :title="$t('ui.entities.group', 2)"
      >
        <div
          v-if="accessSelector.groups.length > 1"
          class="mb-2 text-secondary"
        >
          <Checkbox
            :is-checked="areAllSelected('groups')"
            :label="areAllSelected('groups') ? $t('ui.actions.deselectAll') : $t('ui.actions.selectAll')"
            checkbox-color="utility-gray-500"
            @change="selectAll('groups')"
          />
        </div>
        <div
          v-for="item in accessSelector.groups"
          :key="'group-' + item.id"
          class="mb-1"
        >
          <Checkbox
            :is-checked="accessSelected.groups.includes(item.id)"
            :label="item.name"
            @change="toggleItem('groups', item.id)"
          />
        </div>
        <div
          v-if="accessSelector.groups.length === 0"
          class="text-secondary"
        >
          {{
            $t(
              'ui.entities.locationSelection.sentences.selectManufacturers',
              {
                item: $t('ui.entities.manufacturer', 2)
              },
              2
            )
          }}
        </div>
      </AccordionItem>

      <AccordionItem
        v-model:is-open="accordionState.dealerships"
        :title="$t('ui.entities.dealership', 2)"
      >
        <div
          v-if="accessSelector.dealerships.length > 1"
          class="mb-2 text-secondary"
        >
          <Checkbox
            :is-checked="areAllSelected('dealerships')"
            :label="areAllSelected('dealerships') ? $t('ui.actions.deselectAll') : $t('ui.actions.selectAll')"
            checkbox-color="utility-gray-500"
            @change="selectAll('dealerships')"
          />
        </div>
        <div
          v-for="item in accessSelector.dealerships"
          :key="'dealership-' + item.id"
          class="mb-1"
        >
          <Checkbox
            :is-checked="accessSelected.dealerships.includes(item.id)"
            :label="item.name"
            @change="toggleItem('dealerships', item.id)"
          />
        </div>
        <div
          v-if="accessSelector.dealerships.length === 0"
          class="text-secondary"
        >
          {{ $t('ui.entities.locationSelection.sentences.selectGroups', { item: $t('ui.entities.group', 2) }, 2) }}
        </div>
      </AccordionItem>
    </Accordion>
    <Button
      :label="$t('ui.actions.save')"
      :disabled="accessSelected.dealerships.length > 0 ? false : true"
      class="mt-10"
      @click="handleSave"
    />
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'

// composables
import { useLocationSelector } from '@/composables/useLocationSelector'

// components
import Accordion from '@/components/accordion/Accordion.vue'
import AccordionItem from '@/components/accordion/AccordionItem.vue'
import Checkbox from '@/components/forms/Checkbox.vue'
import Button from '@/components/button/Button.vue'

const emit = defineEmits(['save'])

// Use the composable for access selection logic
const { accessSelected, accessSelector, toggleItem, areAllSelected, selectAll, saveAccessSelection } =
  useLocationSelector()

// UI-specific accordion state
const accordionState = ref({
  regions: true,
  manufacturers: true,
  groups: true,
  dealerships: true
})

// Keep accordion open state in sync with available items
watch(
  accessSelector,
  newArray => {
    try {
      accordionState.value.regions = newArray.regions.length > 1
      accordionState.value.manufacturers = newArray.manufacturers.length > 1
      accordionState.value.groups = newArray.groups.length > 1
      accordionState.value.dealerships = newArray.dealerships.length > 1
    } catch (error) {
      console.error('Failed to update accordion open state', error)
    }
  },
  { immediate: true, deep: true }
)

/**
 * Handles saving the changes and emits the save event
 */
function handleSave() {
  saveAccessSelection()
  emit('save')
}
</script>

<style scoped>
.location-selector {
  inline-size: 100%;
  margin-inline: auto;
  max-inline-size: 600px;
}
</style>
